import React, { Fragment, useMemo, useState } from "react";
import { IFormData, IFormSchema } from "../models/IFormSchema";
import { contextSchema as DefaultContextSchema, ISchemaContext } from "../models/ISchemaContext";
import { ISubmitEvent, AjvError, FormProps, FormValidation } from "@rjsf/core";
import { transformHeaderErrors } from "../utils/transformHeaderErrors";
import { CUSTOM_FORMATS } from "../constants/CustomFormats";
import { Divider } from "semantic-ui-react";
import { SCHEMEA_NS_KEY } from "../constants/Translation";
import { JsonSchemaConfiguration } from "../configurations/JsonSchemaFormConfiguration";
import { EWidgetType } from "../utils/widgetRegistry";
import { useSchemaContext } from "../context/SchemaContext";
import { getSchemaActionTypes } from "../utils/getSchemaActionTypes";

interface FormViewProps<T = IFormData> extends Omit<FormProps<T>, "schema"> {
    formSchema: IFormSchema
    contextSchema?: IFormSchema["schema"];

    formData?: T;
    translations?: { [key: string]: any };

    contextData?: ISchemaContext;
    extraActions?: { [key: string]: any };

    transformFormData?: (formData: Partial<T>, formSchema: IFormSchema) => Partial<T>
    validate?: (formData: any, errors: FormValidation) => FormValidation;

    focusOnError?: boolean;
    configurations: JsonSchemaConfiguration;

    onSubmit: (value: { [key: string]: any }) => void;

}


export const FormView: React.FC<FormViewProps> = ({
    formSchema,
    formData,
    formContext,
    contextData,
    contextSchema = DefaultContextSchema,
    extraActions,
    configurations,

    onError,
    onSubmit,
    onChange,
    transformErrors: customTransformErrors,
    transformFormData: customTransformFormData,
    validate: customValidation,
    ...otherProps

}) => {

    const { translate: t, addTranslationResource } = useSchemaContext() || {};
    const [hasErrors, setHasErrors] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const { translations } = formSchema;
    const Form = useMemo(() => configurations.getForm(EWidgetType.Write), []);


    // If there's translations, add them to the namespace
    if (translations) {
        const keys = Object.keys(translations);
        keys.forEach((key) => {
            addTranslationResource(key, SCHEMEA_NS_KEY, translations[key]);
        });
    };



    const handleError = (errors: any[]) => {
        setHasErrors(errors.length ? true : false);
        if (onError) {
            onError(errors);
        };
    };

    const transformFormData = (formData: Partial<any>, formSchema: IFormSchema) => {
        if (customTransformFormData) {
            formData = customTransformFormData(formData, formSchema);
        }
        return formData;
    };

    const handleSubmit = (form: ISubmitEvent<any>) => {
        const formData: any = transformFormData(form.formData, formSchema);
        if (onSubmit) {
            onSubmit(formData);
        };
        setSubmitted(true);
    };

    const handleChange = (values: any) => {
        if (onChange) {
            onChange(values);
        };
    };

    const validate = (formData: any, errors: FormValidation) => {
        formData = transformFormData(formData, formSchema);
        if (customValidation) {
            errors = customValidation(formData, errors);
        };
        return errors;
    };

    const _transformErrors = (errors: AjvError[]): AjvError[] => {
        if (customTransformErrors) {
            errors = customTransformErrors(errors);
        }
        errors = transformHeaderErrors(formSchema.schema, errors);
        return errors;
    };

    const neutralizedAxtraActions = useMemo(() => {
        const actionTypes = getSchemaActionTypes(formSchema);
        const skipActions = ["remove", "require", "uiReplace", "uiOverride", "uiAppend"]
        const baseActions = actionTypes.reduce<{ [key: string]: any }>((acc, type) => {

            if (!skipActions.includes(type)) {
                return ({ ...acc, [type]: (extraActions || {})[type] || (() => { }) })
            }
            return acc
        }, {});
        return { ...baseActions, ...(extraActions || {}) };
    }, []);



    return (
        <Fragment>
            <Form
                {...otherProps}
                className="ui form SchemaForm"


                formSchema={formSchema}

                formData={formData}
                formContext={{ ...formContext, mode: "form" }}

                onSubmit={handleSubmit}
                onChange={handleChange}
                onError={handleError}
                transformErrors={_transformErrors}

                validate={validate}

                noHtml5Validate={true}
                liveValidate={otherProps.liveValidate || hasErrors || submitted}

                contextData={contextData}
                contextSchema={contextSchema}
                extraActions={neutralizedAxtraActions}


                customFormats={CUSTOM_FORMATS}
                children={otherProps.children || (
                    <Fragment>
                        <Divider hidden />
                        <button type="submit"> {t("Tallenna")} </button>
                    </Fragment>
                )}
            />
        </Fragment>
    );
};